/* eslint-disable prettier/prettier */
import { applyRange } from "./utils";
import { formulas } from "./formulas";
import { calculateExpenses } from "./expenses";

const formatter = new Intl.DateTimeFormat("en", {
    month: "long",
    year: "numeric",
});

const NUMBER_FORMAT = '_("$"* # ##0.00_);_("$"* (# ##0.00);_("$"* "-"??_);_(@_)';

export const payments = (
    paymentSheet,
    billingData,
    attorney,
    mattersData,
    persistedData,
    clioActivitiesKeyed,
    clioActivitiesAttorneyMatters,
    grefs,
    customTab
) => {
    try {
        const customAdjTab = customTab.flatMap(item => {
            if (!item.number || !item.allocations) return [];

            return item.allocations.map(a => {
                // parse as number (fallback to 0)
                const raw = Number(a.custom_value);

                // normalize to 2 decimals but keep as a Number
                const custom_value = Number(raw.toFixed(2));

                return {
                    name: a.name,
                    percentage: a.percentage,
                    custom_value,            // numeric, e.g. 88.9 (will display 88,90 with Excel format)
                    number: item.number
                };
            });
        });
        // Validate inputs to avoid errors
        if (!billingData || !Array.isArray(billingData)) {
            console.error("Invalid billingData:", billingData);
            billingData = [];
        }

        if (!mattersData || !Array.isArray(mattersData)) {
            console.error("Invalid mattersData:", mattersData);
            mattersData = [];
        }

        if (!clioActivitiesKeyed || typeof clioActivitiesKeyed !== "object") {
            console.error("Invalid clioActivitiesKeyed:", clioActivitiesKeyed);
            clioActivitiesKeyed = {};
        }

        if (!clioActivitiesAttorneyMatters || typeof clioActivitiesAttorneyMatters !== "object") {
            console.error("Invalid clioActivitiesAttorneyMatters:", clioActivitiesAttorneyMatters);
            clioActivitiesAttorneyMatters = {};
        }

        // Safely get action invoices
        const actionInvoices = Object.entries(clioActivitiesKeyed)
            .filter((fitem) => {
                return fitem[1] && typeof fitem[1] === "object" && Object.keys(fitem[1]).includes(attorney);
            })
            .map((item) => {
                return item[0];
            });

        // Safely filter matters with proper attorney checking
        const filteredMatters = mattersData.filter((item) => {
            return (
                (item.responsible_attorney &&
                    typeof item.responsible_attorney === "object" &&
                    item.responsible_attorney.name === attorney) ||
                (item.originating_attorney &&
                    typeof item.originating_attorney === "object" &&
                    item.originating_attorney.name === attorney)
            );
        });

        // Safely extract display numbers
        const filteredMattersOnlyName = filteredMatters
            .filter((item) => item && item.display_number)
            .map((item) => item.display_number);



        // FIXED: Safely map billing matters with proper error checking
        const billingMatters = billingData
            .filter(
                (item) =>
                    item &&
                    item.matters &&
                    Array.isArray(item.matters) &&
                    item.matters.length > 0 &&
                    item.matters[0] &&
                    item.matters[0].display_number
            )
            .map((item) => item.matters[0].display_number);

        // Safely get invoked matters with proper checking
        const invoicedMatters =
            clioActivitiesAttorneyMatters &&
                typeof clioActivitiesAttorneyMatters === "object" &&
                clioActivitiesAttorneyMatters.hasOwnProperty(attorney)
                ? Array.from(clioActivitiesAttorneyMatters[attorney])
                : [];

        // Safely filter displayed matters
        const displayedMatters = mattersData.filter((item) => {
            const hasAttorney =
                (item.responsible_attorney &&
                    typeof item.responsible_attorney === "object" &&
                    item.responsible_attorney.name === attorney) ||
                (item.originating_attorney &&
                    typeof item.originating_attorney === "object" &&
                    item.originating_attorney.name === attorney);

            const isInvoiced = item.display_number && invoicedMatters.includes(item.display_number);
            const isInBilling = item.display_number && billingMatters.includes(item.display_number);

            return (hasAttorney || isInvoiced) && isInBilling;
        });

        //max rows for attorney
        const mattersStartRow = 8;
        const MATTERS = displayedMatters.length + mattersStartRow - 1;
        const expensesStartRow = 7;
        const EXPENSES =
            persistedData && persistedData.expenses && attorney in persistedData.expenses
                ? Object.keys(persistedData.expenses[attorney]).length + expensesStartRow - 1
                : 1;

        // Safely check for calculated values
        const calculatedCELGValues =
            grefs &&
            grefs.calculatedCELGValues &&
            grefs.calculatedCELGValues.current &&
            grefs.calculatedCELGValues.current[attorney];

        if (!calculatedCELGValues) {
            calculateExpenses(persistedData, attorney, grefs);
        }

        const keyedBills = {};

        // Safely organize data with proper error checks
        billingData
            .filter((f) => {
                const hasMatter =
                    f &&
                    f.matters &&
                    Array.isArray(f.matters) &&
                    f.matters.length > 0 &&
                    f.matters[0] &&
                    f.matters[0].display_number;

                return (
                    hasMatter &&
                    (filteredMattersOnlyName.includes(f.matters[0].display_number) ||
                        actionInvoices.includes(f.number))
                );
            })
            .forEach((bill) => {
                try {
                    const keyDate = new Date(bill.issued_at);
                    const monthyear = formatter.format(keyDate);

                    if (monthyear in keyedBills) {
                        keyedBills[monthyear].push(bill);
                    } else {
                        keyedBills[monthyear] = [bill];
                    }
                } catch (error) {
                    console.error("Error processing bill:", bill, error);
                }
            });

        paymentSheet.views = [{ state: "frozen", xSplit: 3, ySplit: 7 }];

        const a1 = paymentSheet.getCell("A1");
        const b1 = paymentSheet.getCell("B1");
        a1.value = "Attorney:";
        b1.value = attorney;

        //Merged Column headers
        paymentSheet.mergeCells("A5", "A7");
        const a5 = paymentSheet.getCell("A5");
        a5.value = "Month";
        paymentSheet.mergeCells("B5", "B7");
        const b5 = paymentSheet.getCell("B5");
        b5.value = "MATTER";
        paymentSheet.mergeCells("C5", "C7");
        const c5 = paymentSheet.getCell("C5");
        c5.value = "";
        paymentSheet.mergeCells("D5", "D7");
        const d5 = paymentSheet.getCell("D5");
        d5.value = "Invoice Number";
        paymentSheet.mergeCells("E5", "E7");
        const e5 = paymentSheet.getCell("E5");
        e5.value = "Invoiced Amount Total";
        paymentSheet.mergeCells("F5", "F7");
        const f5 = paymentSheet.getCell("F5");
        f5.value = "Collected (Y/N)";
        paymentSheet.mergeCells("G5", "G7");
        const g5 = paymentSheet.getCell("G5");
        g5.value = "";
        paymentSheet.mergeCells("H5", "H7");
        const h5 = paymentSheet.getCell("H5");
        h5.value = "Invoiced Amount Atty";
        paymentSheet.mergeCells("I5", "I7");
        const i5 = paymentSheet.getCell("I5");
        i5.value = "Originating Atty";
        paymentSheet.mergeCells("J5", "J7");
        const j5 = paymentSheet.getCell("J5");
        j5.value = "Responsible Atty";
        paymentSheet.mergeCells("K5", "K7");
        const k5 = paymentSheet.getCell("K5");
        k5.value = "";
        paymentSheet.mergeCells("L5", "L7");
        const l5 = paymentSheet.getCell("L5");
        l5.value = "Guaranteed";
        paymentSheet.mergeCells("M5", "M7");
        const m5 = paymentSheet.getCell("M5");
        m5.value = "Previously Paid";
        paymentSheet.mergeCells("N5", "N7");
        const n5 = paymentSheet.getCell("N5");
        n5.value = "";
        paymentSheet.mergeCells("O5", "O7");
        const o5 = paymentSheet.getCell("O5");
        o5.value = "On-Collection 55%";
        paymentSheet.mergeCells("P5", "P7");
        const p5 = paymentSheet.getCell("P5");
        p5.value = "Responsible 10%";
        paymentSheet.mergeCells("Q5", "Q7");
        const q5 = paymentSheet.getCell("Q5");
        q5.value = "Originating    25%";
        paymentSheet.mergeCells("R5", "R7");
        const r5 = paymentSheet.getCell("R5");
        r5.value = "Attorney Designated Expenses";
        paymentSheet.mergeCells("S5", "S7");
        const s5 = paymentSheet.getCell("S5");
        s5.value = "Custom Adjustments";
        paymentSheet.mergeCells("T5", "T7");
        const t5 = paymentSheet.getCell("T5");
        t5.value = "Allocations";
        paymentSheet.mergeCells("U5", "U7");
        const u5 = paymentSheet.getCell("U5");
        u5.value = "Previously Paid";
        paymentSheet.mergeCells("V5", "V7");
        const v5 = paymentSheet.getCell("V5");
        v5.value = "Expense Allocation";
        paymentSheet.mergeCells("W5", "W7");
        const w5 = paymentSheet.getCell("W5");
        w5.value = "DUE NOW";
        paymentSheet.mergeCells("X5", "X7");
        const x5 = paymentSheet.getCell("X5");
        x5.value = "DUE LATER";
        paymentSheet.mergeCells("Y5", "Y7");
        const y5 = paymentSheet.getCell("Y5");
        y5.value = "TOTAL DUE";

        paymentSheet.mergeCells("Z5", "AA6");
        const z5 = paymentSheet.getCell("Z5");
        z5.value = "Payment Date";

        const z7 = paymentSheet.getCell("Z7");
        z7.value = "Guaranteed";
        const aa7 = paymentSheet.getCell("AA7");
        aa7.value = "On-Collection";

        paymentSheet.columns = [
            { key: "month", width: 18 },
            { key: "matter", width: 45 },
            { key: "blank1", width: 1 },
            { key: "invoicenumber", width: 25 },
            { key: "invoicedamounttotal", width: 14 },
            { key: "collected", width: 14 },
            { key: "blank2", width: 1 },
            { key: "invoiceamount", width: 14 },
            { key: "originatingatt", width: 14 },
            { key: "responsibleatt", width: 14 },
            { key: "blank3", width: 1 },
            { key: "guaranteed", width: 14 },
            { key: "previouslypaid", width: 14 },
            { key: "blank3", width: 1 },
            { key: "on-collection", width: 14 },
            { key: "responsible", width: 14 },
            { key: "originating", width: 14 },
            { key: "celg", width: 14 },
            { key: "custom-adjustments", width: 14 },
            { key: "blank4", width: 14 },
            { key: "previouslypaid2", width: 14 },
            { key: "total-origination", width: 14 },
            { key: "due-now", width: 14 },
            { key: "due-later", width: 14 },
            { key: "total-due", width: 14 },
            { key: "pd-guaranteed", width: 14 },
            { key: "pd-on-collection", width: 14 },
        ];

        // Safely access calculated values
        const calculatedValues = grefs?.calculatedPaymentValues?.current || {};

        let startRow = 8;
        let row = startRow;
        const sumFormulas = {};
        const totals = {
            total: 0,
            paid: 0,
            guaranteed: 0,
            onCollection: 0,
            responsible: 0,
            originating: 0,
            celg: 0,
            totalOrigination: 0,
            dueNow: 0,
            dueLater: 0,
            totalDue: 0,
        };
        const grandTotals = {
            total: 0,
            paid: 0,
            guaranteed: 0,
            onCollection: 0,
            responsible: 0,
            originating: 0,
            celg: 0,
            totalOrigination: 0,
            dueNow: 0,
            dueLater: 0,
            totalDue: 0,
        };

        //FIRST PASS TO GET ROWS CONTAINING TOTALS BY DATE
        Object.keys(keyedBills)
            .sort((a, b) => {
                return new Date(a) > new Date(b) ? 1 : new Date(a) < new Date(b) ? -1 : 0;
            })
            .forEach((item, i) => {
                row++;
                sumFormulas[item] = { start: row };
                keyedBills[item].forEach((bill, j) => {
                    sumFormulas[item] = { ...sumFormulas[item], end: row };
                    row++;
                });
                row++;
                row++;
            });

        //SECOND PASS TO CALCULATE VALUES EXCEPT R WHICH REQUIRES V TOTAL
        if (grefs && grefs.sumFormulas && grefs.sumFormulas.current) {
            grefs.sumFormulas.current = sumFormulas;
        }
        row = startRow;

        Object.keys(keyedBills)
            .sort((a, b) => {
                return new Date(a) > new Date(b) ? 1 : new Date(a) < new Date(b) ? -1 : 0;
            })
            .forEach((item, i) => {
                //MONTH HEADER
                paymentSheet.getRow(row).values = [item];
                paymentSheet.getCell(`A${row}`).font = {
                    name: "Calibri",
                    size: 12,
                    bold: "true",
                };
                applyRange(paymentSheet, `A${row}:AA${row}`, "fill", {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "D9D9D9" },
                });
                row++;

                keyedBills[item].forEach((bill, j) => {
                    try {
                        // FIXED: Safely check if bill has matters data
                        if (
                            !bill.matters ||
                            !Array.isArray(bill.matters) ||
                            bill.matters.length === 0 ||
                            !bill.matters[0]
                        ) {
                            console.warn(`Bill ${bill.number} missing matters data, skipping`);
                            return; // Skip this bill
                        }

                        const matterDisplayNumber = bill.matters[0].display_number;
                        if (!matterDisplayNumber) {
                            console.warn(`Bill ${bill.number} has no display_number, skipping`);
                            return; // Skip this bill
                        }

                        // Safely get persisted data
                        const persisted =
                            bill.number in persistedData.payments
                                ? // Try to find an entry specifically for this attorney
                                (Array.isArray(persistedData.payments[bill.number])
                                    ? persistedData.payments[bill.number].find(
                                        (payment) => payment.attorney === attorney
                                    )
                                    : persistedData.payments[bill.number].attorney === attorney
                                        ? persistedData.payments[bill.number]
                                        : null) || {
                                    InvoicedAmountAtty: 0,
                                    CustomAdjustment: 0,
                                    CustomAdjustmentPrevPaid: "N",
                                    GuaranteedPrevPaid: "N",
                                }
                                : {
                                    InvoicedAmountAtty: 0,
                                    CustomAdjustment: 0,
                                    CustomAdjustmentPrevPaid: "N",
                                    GuaranteedPrevPaid: "N",
                                };

                        // Safely get persisted matters
                        const persistedMatters =
                            matterDisplayNumber in persistedData.matters
                                ? Object.values(persistedData.matters[matterDisplayNumber]).find(
                                    (matter) => matter.attorney === attorney || !matter.attorney
                                ) || persistedData.matters[matterDisplayNumber]
                                : {
                                    Guaranteed: "",
                                    "On-Collection": "",
                                    Originating: "",
                                    Responsible: "",
                                };
                        // Add this helper function at the top of your payments.js file
                        function calculateBillTotalExcludingExpenses(bill) {
                            try {
                                // Skip if bill is invalid
                                if (!bill || !bill.line_items || !Array.isArray(bill.line_items)) {
                                    return bill?.total || 0;
                                }

                                // Calculate total of non-expense items only
                                const nonExpenseTotal = bill.line_items
                                    .filter((item) => {
                                        if (!item) return false;

                                        // Check if item is NOT an expense
                                        return !(
                                            (item.type && item.type.toLowerCase().includes("expense")) ||
                                            (item.category && item.category.toLowerCase().includes("expense")) ||
                                            item.expense === true
                                        );
                                    })
                                    .reduce((total, item) => total + (item.total || 0), 0);

                                return nonExpenseTotal;
                            } catch (error) {
                                console.error("Error calculating non-expense total:", error);
                                return bill?.total || 0;
                            }
                        }

                        const issuedDate = formatter.format(new Date(bill.issued_at));
                        function findAllocation(result, number, name) {
                            const matches = result.filter(
                                item => item.number === number && item.name === name
                            );

                            if (matches.length === 0) return 0;

                            const values = matches
                                .map(i => Number(i.custom_value))
                                .filter(v => !isNaN(v));

                            // Only one value → return it (number)
                            if (values.length === 1) {
                                return values[0];
                            }

                            // More than one → sum them
                            const total = values.reduce((sum, val) => sum + val, 0);
                            return total;
                        }

                        // Usage
                        const custom2 = findAllocation(customAdjTab, bill.number, attorney);



                        // Safely get revenue amount
                        const revenueAmount =
                            clioActivitiesKeyed[bill.number] && clioActivitiesKeyed[bill.number][attorney]
                                ? clioActivitiesKeyed[bill.number][attorney]
                                : 0;

                        //VALUES
                        paymentSheet.getCell(`A${row}`).value = issuedDate;
                        paymentSheet.getCell(`B${row}`).value = matterDisplayNumber;
                        // paymentSheet.getCell(`C${row}`).value = "";
                        paymentSheet.getCell(`D${row}`).value = bill.number;
                        // paymentSheet.getCell(`E${row}`).value = bill.total;
                        // To this:
                        const totalWithoutExpenses = calculateBillTotalExcludingExpenses(bill);
                        paymentSheet.getCell(`E${row}`).value = totalWithoutExpenses;
                        paymentSheet.getCell(`F${row}`).value = bill.state === "paid" ? "Y" : "N";
                        // paymentSheet.getCell(`G${row}`).value = "";
                        paymentSheet.getCell(`H${row}`).value = revenueAmount;
                        //custom2
                        const cell = paymentSheet.getCell(`T${row}`);
                        const num = Number(custom2 * -1);

                        // CASE 1 → When value is 0
                        if (!num) {
                            cell.value = 0;
                            cell.numFmt = '"$ "0';     // displays: $ 0
                        }
                        // CASE 2 → When value is NOT 0
                        else {
                            cell.value = num;
                            cell.numFmt = '"$"#,##0.00';   // displays: $ -12.34
                        }
                        // paymentSheet.getCell(`T${row}`).numFmt = '"$" # ##0,00';
                        /*********GET FROM STORE***************/
                        paymentSheet.getCell(`M${row}`).value = persisted.GuaranteedPrevPaid;
                        paymentSheet.getCell(`S${row}`).value =
                            persisted.CustomAdjustment === "" ? "" : +persisted.CustomAdjustment;
                        paymentSheet.getCell(`U${row}`).value = persisted.CustomAdjustmentPrevPaid;
                        paymentSheet.getCell(`Z${row}`).value = persisted.GuaranteedPaymentDate
                            ? new Date(persisted.GuaranteedPaymentDate)
                            : "";
                        paymentSheet.getCell(`AA${row}`).value = persisted.OnCollectionPaymentDate
                            ? new Date(persisted.OnCollectionPaymentDate)
                            : "";

                        //FILL
                        applyRange(paymentSheet, `I${row}:Y${row}`, "fill", {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "F8CBAD" },
                        });

                        paymentSheet.getCell(`B${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: {
                                argb: filteredMattersOnlyName.includes(matterDisplayNumber) ? "C6E0B4" : "FFFFFF",
                            },
                        };
                        paymentSheet.getCell(`C${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "BFBFBF" },
                        };
                        paymentSheet.getCell(`F${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFF00" },
                        };
                        paymentSheet.getCell(`G${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "BFBFBF" },
                        };
                        paymentSheet.getCell(`K${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "BFBFBF" },
                        };
                        paymentSheet.getCell(`M${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFF00" },
                        };
                        paymentSheet.getCell(`N${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "BFBFBF" },
                        };
                        paymentSheet.getCell(`S${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFFFF" },
                        };
                        paymentSheet.getCell(`T${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "BFBFBF" },
                        };
                        paymentSheet.getCell(`U${row}`).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFF00" },
                        };
                        //ALIGNMENT
                        paymentSheet.getCell(`D${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        paymentSheet.getCell(`T${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        paymentSheet.getCell(`F${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        paymentSheet.getCell(`I${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        paymentSheet.getCell(`J${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        paymentSheet.getCell(`M${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        paymentSheet.getCell(`U${row}`).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        //FONT
                        paymentSheet.getCell(`D${row}`).font = {
                            name: "Calibri",
                            size: 12,
                            bold: "true",
                        };
                        //format
                        paymentSheet.getCell(`E${row}`).numFmt = NUMBER_FORMAT;
                        paymentSheet.getCell(`H${row}`).numFmt = NUMBER_FORMAT;
                        paymentSheet.getCell(`L${row}`).numFmt = NUMBER_FORMAT;
                        applyRange(paymentSheet, `O${row}:S${row}`, "numFmt", NUMBER_FORMAT);
                        applyRange(paymentSheet, `V${row}:Y${row}`, "numFmt", NUMBER_FORMAT);
                        applyRange(paymentSheet, `Z${row}:AA${row}`, "numFmt", "yyyy/mm/dd");
                        //FORMULAS
                        paymentSheet.getCell(`I${row}`).value = {
                            formula: `IF((ISNUMBER(MATCH($B${row},Matters!$B$8:$B$${MATTERS},0))=TRUE),INDEX(Matters!$C$8:$C$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)),"N")`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "I",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`J${row}`).value = {
                            formula: `IF((ISNUMBER(MATCH($B${row},Matters!$B$8:$B$${MATTERS},0))=TRUE),INDEX(Matters!$D$8:$D$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)),"N")`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "J",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };

                        paymentSheet.getCell(`L${row}`).value = {
                            formula: `IF(($I${row}="N"),+$H${row}*IF((ISNUMBER(MATCH($B${row},Matters!$B$8:$B$${MATTERS},0))=TRUE),IF((ISNUMBER(INDEX(Matters!$F$8:$F$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)))=TRUE),INDEX(Matters!$F$8:$F$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)),0.55),0.55),0)`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "L",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`O${row}`).value = {
                            formula: `IF(($I${row}="Y"),+$H${row}*IF((ISNUMBER(MATCH($B${row},Matters!$B$8:$B$${MATTERS},0))=TRUE),IF((ISNUMBER(INDEX(Matters!$G$8:$G$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)))=TRUE),INDEX(Matters!$G$8:$G$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)),0.55), 0.55),0)`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "O",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`P${row}`).value = {
                            formula: `IF(($J${row}="Y"),+$E${row}*IF((ISNUMBER(MATCH($B${row},Matters!$B$8:$B$${MATTERS},0))=TRUE),IF((ISNUMBER(INDEX(Matters!$H$8:$H$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)))=TRUE),INDEX(Matters!$H$8:$H$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)),0.1), 0.1),0)`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "P",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`Q${row}`).value = {
                            formula: `IF(($I${row}="Y"),+$E${row}*IF((ISNUMBER(MATCH($B${row},Matters!$B$8:$B$${MATTERS},0))=TRUE),IF((ISNUMBER(INDEX(Matters!$I$8:$I$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)))=TRUE),INDEX(Matters!$I$8:$I$${MATTERS},MATCH($B${row},Matters!$B$8:$B$${MATTERS},0)),0.25), 0.25),0)`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "Q",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };

                        paymentSheet.getCell(`V${row}`).value = {
                            formula: `MAX(L${row},SUM(O${row}:Q${row}))`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "V",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`W${row}`).value = {
                            formula: `
    IF($M${row}="Y",0,IF(ISNUMBER($L${row}),$L${row},0))
    +IF($U${row}="Y",0,
        IF($F${row}="N",0,
           $O${row}+$P${row}+$Q${row}+$R${row}
           +IF(ISNUMBER($S${row}),$S${row},0)
           +$T${row}
        )
     )
  `,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "W",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`X${row}`).value = {
                            formula: `
    IF($U${row}="Y",0,
       IF($F${row}="Y",0,
          $O${row}+$P${row}+$Q${row}+$R${row}
          +IF(ISNUMBER($S${row}),$S${row},0)
          +$T${row}
       )
    )
  `,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "X",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };
                        paymentSheet.getCell(`Y${row}`).value = {
                            formula: `W${row}+X${row}`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "Y",
                                attorney,
                                persisted,
                                persistedMatters,
                                persistedData,
                                grefs
                            ),
                        };

                        // Safely update totals with proper error checking
                        // totals.total += bill.total || 0;
                        // grandTotals.total += bill.total || 0;
                        totals.total += totalWithoutExpenses;
                        grandTotals.total += totalWithoutExpenses;
                        totals.paid += revenueAmount || 0;
                        grandTotals.paid += revenueAmount || 0;

                        // Safely access calculated values with proper error checking
                        const rowL =
                            calculatedValues && calculatedValues["L"] ? calculatedValues["L"][row] || 0 : 0;
                        const rowO =
                            calculatedValues && calculatedValues["O"] ? calculatedValues["O"][row] || 0 : 0;
                        const rowP =
                            calculatedValues && calculatedValues["P"] ? calculatedValues["P"][row] || 0 : 0;
                        const rowQ =
                            calculatedValues && calculatedValues["Q"] ? calculatedValues["Q"][row] || 0 : 0;
                        const rowV =
                            calculatedValues && calculatedValues["V"] ? calculatedValues["V"][row] || 0 : 0;
                        const rowW =
                            calculatedValues && calculatedValues["W"] ? calculatedValues["W"][row] || 0 : 0;
                        const rowX =
                            calculatedValues && calculatedValues["X"] ? calculatedValues["X"][row] || 0 : 0;
                        const rowY =
                            calculatedValues && calculatedValues["Y"] ? calculatedValues["Y"][row] || 0 : 0;

                        totals.guaranteed += rowL;
                        grandTotals.guaranteed += rowL;
                        totals.onCollection += rowO;
                        grandTotals.onCollection += rowO;
                        totals.responsible += rowP;
                        grandTotals.responsible += rowP;
                        totals.originating += rowQ;
                        grandTotals.originating += rowQ;
                        totals.totalOrigination += rowV;
                        grandTotals.totalOrigination += rowV;
                        totals.dueNow += rowW;
                        grandTotals.dueNow += rowW;
                        totals.dueLater += rowX;
                        grandTotals.dueLater += rowX;
                        totals.totalDue += rowY;
                        grandTotals.totalDue += rowY;
                    } catch (error) {
                        console.error(`Error processing bill in row ${row}:`, error);
                    }

                    row++;
                });

                //TOTALS
                paymentSheet.getCell(`E${row}`).value = {
                    formula: `SUM(E${sumFormulas[item].start}:E${sumFormulas[item].end})`,
                    result: totals.total,
                };
                paymentSheet.getCell(`H${row}`).value = {
                    formula: `SUM(H${sumFormulas[item].start}:H${sumFormulas[item].end})`,
                    result: totals.paid,
                };
                paymentSheet.getCell(`L${row}`).value = {
                    formula: `SUM(L${sumFormulas[item].start}:L${sumFormulas[item].end})`,
                    result: totals.guaranteed,
                };
                paymentSheet.getCell(`O${row}`).value = {
                    formula: `SUM(O${sumFormulas[item].start}:O${sumFormulas[item].end})`,
                    result: totals.onCollection,
                };
                paymentSheet.getCell(`P${row}`).value = {
                    formula: `SUM(P${sumFormulas[item].start}:P${sumFormulas[item].end})`,
                    result: totals.responsible,
                };
                paymentSheet.getCell(`Q${row}`).value = {
                    formula: `SUM(Q${sumFormulas[item].start}:Q${sumFormulas[item].end})`,
                    result: totals.originating,
                };
                paymentSheet.getCell(`V${row}`).value = {
                    formula: `SUM(V${sumFormulas[item].start}:V${sumFormulas[item].end})`,
                    result: totals.totalOrigination,
                };
                sumFormulas[item] = {
                    ...sumFormulas[item],
                    totalOrigination: totals.totalOrigination,
                };
                paymentSheet.getCell(`W${row}`).value = {
                    formula: `SUM(W${sumFormulas[item].start}:W${sumFormulas[item].end})`,
                    result: totals.dueNow,
                };
                paymentSheet.getCell(`X${row}`).value = {
                    formula: `SUM(X${sumFormulas[item].start}:X${sumFormulas[item].end})`,
                    result: totals.dueLater,
                };
                paymentSheet.getCell(`Y${row}`).value = {
                    formula: `SUM(Y${sumFormulas[item].start}:Y${sumFormulas[item].end})`,
                    result: totals.totalDue,
                };

                applyRange(paymentSheet, `A${row}:AA${row}`, "font", {
                    name: "Calibri",
                    size: 12,
                    bold: "true",
                });
                applyRange(paymentSheet, `A${row}:AA${row}`, "fill", {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "D9D9D9" },
                });

                paymentSheet.getCell(`E${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`H${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`L${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`O${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`P${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`Q${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`V${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`W${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`X${row}`).numFmt = NUMBER_FORMAT;
                paymentSheet.getCell(`Y${row}`).numFmt = NUMBER_FORMAT;
                Object.keys(totals).forEach((tot) => (totals[tot] = 0));

                row++;
                row++;
            });

        //THIRD PASS TO CALCULATE R WHICH REQUIRES V TOTAL
        if (grefs && grefs.sumFormulas && grefs.sumFormulas.current) {
            grefs.sumFormulas.current = sumFormulas;
        }
        row = startRow;

        Object.keys(keyedBills)
            .sort((a, b) => {
                return new Date(a) > new Date(b) ? 1 : new Date(a) < new Date(b) ? -1 : 0;
            })
            .forEach((item, i) => {
                row++;
                keyedBills[item].forEach((bill, j) => {
                    try {
                        // FIXED: Ensure bill has valid matters data before accessing
                        if (
                            !bill.matters ||
                            !Array.isArray(bill.matters) ||
                            bill.matters.length === 0 ||
                            !bill.matters[0]
                        ) {
                            row++;
                            return; // Skip this bill
                        }

                        const totRow = sumFormulas[item].end + 1;

                        paymentSheet.getCell(`R${row}`).value = {
                            formula: `IF(ISNUMBER(INDEX(Expenses!$F$7:$F$${EXPENSES},MATCH($A${row},Expenses!$B$7:$B$${EXPENSES},))*$V${row}/V$${totRow}),INDEX(Expenses!$F$7:$F$${EXPENSES},MATCH($A${row},Expenses!$B$7:$B$${EXPENSES},))*$V${row}/V$${totRow},0)`,
                            result: formulas(
                                displayedMatters,
                                paymentSheet,
                                row,
                                "R",
                                attorney,
                                {},
                                {},
                                persistedData,
                                grefs,
                                item
                            ),
                        };

                        // Safely access calculated values with null checks
                        const rowR =
                            calculatedValues && calculatedValues["R"] ? calculatedValues["R"][row] || 0 : 0;
                        totals.celg += rowR;
                        grandTotals.celg += rowR;
                    } catch (error) {
                        console.error(`Error processing row ${row} for "R" calculation:`, error);
                    }

                    row++;
                });

                //TOTALS
                paymentSheet.getCell(`R${row}`).value = {
                    formula: `SUM(R${sumFormulas[item].start}:R${sumFormulas[item].end})`,
                    result: totals.celg,
                };
                paymentSheet.getCell(`R${row}`).numFmt = NUMBER_FORMAT;
                Object.keys(totals).forEach((tot) => (totals[tot] = 0));

                row++;
                row++;
            });

        row += 2;

        //GRAND TOTALS
        paymentSheet.getCell(`A${row}`).value = "Total";

        // When the attorney/group matched no bills there are no month subtotal rows,
        // and the join produced the literal "SUM()" — invalid Excel syntax that makes
        // the downloaded file prompt for repair. Write the plain total instead.
        const grandTotalCell = (column, total) => {
            const refs = Object.values(sumFormulas).map((item) => column + (item.end + 1));

            return refs.length > 0 ? { formula: `SUM(${refs.join(",")})`, result: total } : total;
        };

        paymentSheet.getCell(`E${row}`).value = grandTotalCell("E", grandTotals.total);
        paymentSheet.getCell(`H${row}`).value = grandTotalCell("H", grandTotals.paid);
        paymentSheet.getCell(`L${row}`).value = grandTotalCell("L", grandTotals.guaranteed);
        paymentSheet.getCell(`O${row}`).value = grandTotalCell("O", grandTotals.onCollection);
        paymentSheet.getCell(`P${row}`).value = grandTotalCell("P", grandTotals.responsible);
        paymentSheet.getCell(`Q${row}`).value = grandTotalCell("Q", grandTotals.originating);
        paymentSheet.getCell(`R${row}`).value = grandTotalCell("R", grandTotals.celg);
        paymentSheet.getCell(`V${row}`).value = grandTotalCell("V", grandTotals.totalOrigination);
        paymentSheet.getCell(`W${row}`).value = grandTotalCell("W", grandTotals.dueNow);
        paymentSheet.getCell(`X${row}`).value = grandTotalCell("X", grandTotals.dueLater);
        paymentSheet.getCell(`Y${row}`).value = grandTotalCell("Y", grandTotals.totalDue);

        applyRange(paymentSheet, `A${row}:AA${row}`, "font", {
            name: "Calibri",
            size: 12,
            bold: "true",
        });
        paymentSheet.getCell(`E${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`H${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`L${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`O${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`P${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`Q${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`R${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`V${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`W${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`X${row}`).numFmt = NUMBER_FORMAT;
        paymentSheet.getCell(`Y${row}`).numFmt = NUMBER_FORMAT;
        Object.keys(totals).forEach((tot) => (totals[tot] = 0));

        applyRange(paymentSheet, `A${row}:AA${row}`, "border", {
            top: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "double", color: { argb: "FF000000" } },
        });

        applyRange(paymentSheet, `A${row}:AA${row}`, "fill", {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D0CECE" },
        });

        //ALIGNENT
        a5.alignment = {
            vertical: "middle",
            horizontal: "left",
        };
        applyRange(paymentSheet, "B5:Z5", "alignment", {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
        });
        applyRange(paymentSheet, "Z7:AA7", "alignment", {
            vertical: "top",
            horizontal: "center",
        });

        //FILL
        applyRange(paymentSheet, "A5:Z5", "fill", {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "DDEBF7" },
        });
        paymentSheet.getCell(`F5`).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF00" },
        };
        applyRange(paymentSheet, "Z7:AA7", "fill", {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "DDEBF7" },
        });

        //BORDER
        applyRange(paymentSheet, "A5:Y5", "border", {
            top: { style: "thick", color: { argb: "FF000000" } },
            bottom: { style: "thick", color: { argb: "FF000000" } },
        });
        applyRange(paymentSheet, "Z5:AA5", "border", {
            top: { style: "thick", color: { argb: "FF000000" } },
        });
        applyRange(paymentSheet, "Z7:AA7", "border", {
            bottom: { style: "thick", color: { argb: "FF000000" } },
        });
        //FONTS
        applyRange(paymentSheet, "A1:B1", "font", {
            name: "Calibri",
            size: 12,
            bold: "true",
        });

        //HIDE
        const VCol = paymentSheet.getColumn("V");
        VCol.hidden = true;

    } catch (error) {
        console.error("Error in payments function:", error);
        // Add a visible error message in the sheet
        const errorCell = paymentSheet.getCell("A1");
        errorCell.value = `Error generating report: ${error.message}`;
        errorCell.font = {
            name: "Calibri",
            size: 12,
            bold: true,
            color: { argb: "FF0000" },
        };
    }
};
